import { Cesium } from 'app/cesium';
import { Asset } from 'app/shared/collections/asset';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { IpDevice } from 'app/shared/collections/ip_device';
import { IpDeviceCollection } from 'app/shared/collections/ip_device_collection.service';
import { LocationStatCollection } from 'app/shared/collections/location_stat_collection.service';
import { Radio } from 'app/shared/collections/radio';
import { RadioCollection } from 'app/shared/collections/radio_collection.service';
import { cartesian3ToDegrees, LatLon } from 'app/shared/utilities/mapping_utils';
import { isRadioStatChannelAFrequency } from 'app/shared/utils';
import { Task } from 'app/shared/workers/TaskProcessorInterfaces';
import { IpDeviceStatCollection } from 'app/views/view_types/network/collections/ip_device_stat_collection.service';
import { RadioStat } from 'app/views/view_types/network/collections/radio_stat';
import { RFAntFileCollection } from 'app/views/view_types/network/features/rf_planning/collections/rf_ant_file_collection.service';
import { RFTransmitter } from 'app/views/view_types/network/features/rf_planning/collections/rf_transmitter';
import { RF_TRANSMITTER_TYPE_OPTIONS } from 'app/views/view_types/network/features/rf_planning/collections/rf_transmitter_type_options';
import { IgnoredTransmitterReason } from 'app/views/view_types/network/features/rf_planning/rf_coverage/rf_plan_generator_result';
import { RFTransmitterProcessor } from 'app/views/view_types/network/features/rf_planning/rf_coverage/RFTransmitterProcessor';
import {
    AutoAddRFPlanConfig,
    CREATE_PER,
    RADIO_CHANNEL_OPTION,
} from 'app/views/view_types/network/features/rf_planning/view/rf_plan_auto_add_transmitters.component';
import {
    CHANNEL_CUT_OFF_2_4_GHZ,
    CHANNEL_CUT_OFF_5_GHZ,
    FREQUENCY_MAX_2_4_GHZ,
    FREQUENCY_MAX_5_GHZ,
    FREQUENCY_MIN_2_4_GHZ,
    FREQUENCY_MIN_5_GHZ,
} from 'app/views/view_types/network/shared/connections/connection.service';
import flatten from 'lodash/flatten';
import intersection from 'lodash/intersection';
import isFinite from 'lodash/isFinite';
import sortBy from 'lodash/sortBy';

const CHAN_TO_FREQ = {
    '1': 2412,
    '2': 2417,
    '3': 2422,
    '4': 2427,
    '5': 2432,
    '6': 2437,
    '7': 2442,
    '8': 2447,
    '9': 2452,
    '10': 2457,
    '11': 2462,
    '12': 2467,
    '13': 2472,
    '14': 2484,
    '16': 5080,
    '17': 5085,
    '18': 5090,
    '19': 5095,
    '20': 5100,
    '21': 5105,
    '22': 5110,
    '23': 5115,
    '24': 5120,
    '25': 5125,
    '26': 5130,
    '27': 5135,
    '28': 5140,
    '29': 5145,
    '30': 5150,
    '31': 5155,
    '32': 5160,
    '33': 5165,
    '34': 5170,
    '35': 5175,
    '36': 5180,
    '37': 5185,
    '38': 5190,
    '39': 5195,
    '40': 5200,
    '41': 5205,
    '42': 5210,
    '43': 5215,
    '44': 5220,
    '45': 5225,
    '46': 5230,
    '47': 5235,
    '48': 5240,
    '49': 5245,
    '50': 5250,
    '51': 5255,
    '52': 5260,
    '53': 5265,
    '54': 5270,
    '55': 5275,
    '56': 5280,
    '57': 5285,
    '58': 5290,
    '59': 5295,
    '60': 5300,
    '61': 5305,
    '62': 5310,
    '63': 5315,
    '64': 5320,
    '65': 5325,
    '66': 5330,
    '67': 5335,
    '68': 5340,
    '69': 5345,
    '70': 5350,
    '71': 5355,
    '72': 5360,
    '73': 5365,
    '74': 5370,
    '75': 5375,
    '76': 5380,
    '77': 5385,
    '78': 5390,
    '79': 5395,
    '80': 5400,
    '81': 5405,
    '82': 5410,
    '83': 5415,
    '84': 5420,
    '85': 5425,
    '86': 5430,
    '87': 5435,
    '88': 5440,
    '89': 5445,
    '90': 5450,
    '91': 5455,
    '92': 5460,
    '93': 5465,
    '94': 5470,
    '95': 5475,
    '96': 5480,
    '97': 5485,
    '98': 5490,
    '99': 5495,
    '100': 5500,
    '101': 5505,
    '102': 5510,
    '103': 5515,
    '104': 5520,
    '105': 5525,
    '106': 5530,
    '107': 5535,
    '108': 5540,
    '109': 5545,
    '110': 5550,
    '111': 5555,
    '112': 5560,
    '113': 5565,
    '114': 5570,
    '115': 5575,
    '116': 5580,
    '117': 5585,
    '118': 5590,
    '119': 5595,
    '120': 5600,
    '121': 5605,
    '122': 5610,
    '123': 5615,
    '124': 5620,
    '125': 5625,
    '126': 5630,
    '127': 5635,
    '128': 5640,
    '129': 5645,
    '130': 5650,
    '131': 5655,
    '132': 5660,
    '133': 5665,
    '134': 5670,
    '135': 5675,
    '136': 5680,
    '137': 5685,
    '138': 5690,
    '139': 5695,
    '140': 5700,
    '141': 5705,
    '142': 5710,
    '143': 5715,
    '144': 5720,
    '145': 5725,
    '146': 5730,
    '147': 5735,
    '148': 5740,
    '149': 5745,
    '150': 5750,
    '151': 5755,
    '152': 5760,
    '153': 5765,
    '154': 5770,
    '155': 5775,
    '156': 5780,
    '157': 5785,
    '158': 5790,
    '159': 5795,
    '160': 5800,
    '161': 5805,
    '162': 5810,
    '163': 5815,
    '164': 5820,
    '165': 5825,
    '169': 5845,
    '172': 5860,
    '173': 5865,
    '174': 5870,
    '176': 5880,
    '177': 5885,
    '178': 5890,
    '180': 5900,
    '182': 5910,
    '183': 5915,
    '184': 5920,
    '187': 5935,
    '188': 5940,
    '189': 5945,
    '192': 5960,
    '196': 5980,
};

export interface BoundRFTransmitterDefinition {
    assetId: number;
    radioId: number;
    name: string;
}

export enum Frequency {
    _2_4_GHZ,
    _5_GHZ,
}

function getLocationForRFTransmitter(
    rfTransmitter: RFTransmitter,
    locationStatCollection: LocationStatCollection,
    time: Cesium.JulianDate,
): LatLon {
    if (rfTransmitter.assetId) {
        const cartesian3 = locationStatCollection.getPositionAtTime(
            rfTransmitter.assetId,
            time,
            false,
        );
        return cartesian3 ? cartesian3ToDegrees(cartesian3) : undefined;
    } else {
        return rfTransmitter.location
            ? { latitude: rfTransmitter.latitude, longitude: rfTransmitter.longitude }
            : undefined;
    }
}

enum TxHeightSource {
    default,
    asset,
    asset_type,
    transmitter,
}

function getHeightForRFTransmitter(
    rfTransmitter: RFTransmitter,
    assetCollection: AssetCollection,
): { txHeight: number; source: TxHeightSource } {
    if (!rfTransmitter) {
        return { txHeight: null, source: null };
    }
    // transmitter height
    const isTransmitterHeightValid = isFinite(rfTransmitter.txHeight);
    if (isTransmitterHeightValid) {
        return {
            txHeight: rfTransmitter.txHeight,
            source: TxHeightSource.transmitter,
        };
    }
    // otherwise associated asset height
    const hasAsset = rfTransmitter.assetId;
    const asset = hasAsset ? assetCollection.get(rfTransmitter.assetId) : null;
    const isAssetHeightValid = asset ? isFinite(asset.height) : false;
    if (isAssetHeightValid) {
        return { txHeight: asset.height, source: TxHeightSource.asset };
    }

    // otherwise associated asset type height
    const isAssetTypeHeightValid = isFinite(asset?.type?.height);
    if (isAssetTypeHeightValid) {
        return { txHeight: asset.type.height, source: TxHeightSource.asset_type };
    }

    // otherwise default transmitter height
    return { txHeight: rfTransmitter.defaultTxHeight || 0, source: TxHeightSource.default };
}

function getRadioStatAtTime(
    radioId: number,
    radioCollection: RadioCollection,
    ipDeviceStatCollection: IpDeviceStatCollection,
    time: Cesium.JulianDate,
): RadioStat {
    const radio = radioCollection.get(radioId);
    const ipDeviceStats = radio ? ipDeviceStatCollection.getStats(radio.ipDeviceId, time) : [];
    if (ipDeviceStats.length > 1) {
        throw new Error('expected no more than 1 IP Device Stat');
    }
    const ipStat = ipDeviceStats[0];
    return ipStat ? ipStat.radioStats.find((s) => s.radioId === radioId) : undefined;
}

function getRadioPropertiesForRFTransmitter(
    rfTransmitter: RFTransmitter,
    ipDeviceStatCollection: IpDeviceStatCollection,
    radioCollection: RadioCollection,
    time: Cesium.JulianDate,
): { txPowerDbm: number; frequencyMhz: number } {
    if (rfTransmitter.radioId) {
        const radioStat = getRadioStatAtTime(
            rfTransmitter.radioId,
            radioCollection,
            ipDeviceStatCollection,
            time,
        );
        const freq = radioStat ? getAbsoluteFrequencyForChannel(radioStat.channel) : undefined;
        return {
            txPowerDbm: radioStat ? radioStat.tx_power : undefined,
            frequencyMhz: freq,
        };
    } else {
        return {
            txPowerDbm: rfTransmitter.txPowerMilliwatts
                ? calculatedBmFrommW(rfTransmitter.txPowerMilliwatts)
                : undefined,
            frequencyMhz: rfTransmitter.frequencyMhz,
        };
    }
}

function radioIdsForAsset(asset: Asset): Array<number> {
    return Object.values(asset.ipDevices || {}).reduce(
        (radioIds, ipDevice) =>
            radioIds.concat(Object.values(ipDevice.radios || {}).map((r: Radio) => r.id)),
        [],
    );
}

export function createProcessorFromRfTransmitter(
    rfTransmitter: RFTransmitter,
    locationStatCollection: LocationStatCollection,
    ipDeviceStatCollection: IpDeviceStatCollection,
    assetCollection: AssetCollection,
    radioCollection: RadioCollection,
    antFileCollection: RFAntFileCollection,
    time: Cesium.JulianDate | null,
): {
    rfTransmitterProcessor: RFTransmitterProcessor | undefined;
    error: IgnoredTransmitterReason | undefined;
} {
    const location = getLocationForRFTransmitter(rfTransmitter, locationStatCollection, time);
    const { txPowerDbm, frequencyMhz } = getRadioPropertiesForRFTransmitter(
        rfTransmitter,
        ipDeviceStatCollection,
        radioCollection,
        time,
    );
    if (!location) {
        return {
            rfTransmitterProcessor: undefined,
            error: IgnoredTransmitterReason.NO_ASSET_LOCATION,
        };
    }
    if (!txPowerDbm || !frequencyMhz) {
        return {
            rfTransmitterProcessor: undefined,
            error: IgnoredTransmitterReason.NO_RADIO_POWER,
        };
    }
    const shouldHaveAntFile =
        rfTransmitter.rfAntFileId && rfTransmitter.type === RF_TRANSMITTER_TYPE_OPTIONS.antFile;
    const antFile = shouldHaveAntFile
        ? antFileCollection.get(rfTransmitter.rfAntFileId)
        : undefined;
    if (shouldHaveAntFile && !antFile) {
        return {
            rfTransmitterProcessor: undefined,
            error: IgnoredTransmitterReason.NO_ANT_FILE_FOUND,
        };
    }
    const antFileData = antFile ? antFile.fileData : undefined;

    const { txHeight } = getHeightForRFTransmitter(rfTransmitter, assetCollection);

    const trans = new RFTransmitterProcessor(
        location.latitude,
        location.longitude,
        txHeight,
        rfTransmitter.groundHeight,
        frequencyMhz,
        txPowerDbm,
        rfTransmitter.txGain,
        rfTransmitter.rxGain,
        rfTransmitter.rxHeight,
        rfTransmitter.rssiCutoff,
        rfTransmitter.type,
        rfTransmitter.verticalAngleDeg,
        rfTransmitter.horizontalAngleDeg,
        rfTransmitter.beamWidthDeg,
        rfTransmitter.beamHeightDeg,
        antFileData,
    );
    return {
        rfTransmitterProcessor: trans,
        error: undefined,
    };
}

function handleCollectionGetException(
    getFn: CallableFunction,
    id: number,
): Asset | IpDevice | null {
    let imsItem = null;

    try {
        imsItem = getFn(id);
    } catch (e) {
        // noop
    }

    if (!!!imsItem) {
        imsItem = null;
    }

    return imsItem;
}

function createAutoTransmittersForAssetsOnly(
    assetTypeIds: number[],
    assetCollection: AssetCollection,
): Asset[] {
    return assetTypeIds.length
        ? assetCollection.getAllAsArray().filter((asset) => {
              return assetTypeIds.includes(asset.typeId);
          })
        : assetCollection.getAllAsArray();
}

function createAutoTransmitterseForAssetsAndRadios(
    radioCollection: RadioCollection,
    ipDeviceCollection: IpDeviceCollection,
    assetCollection: AssetCollection,
    ipDeviceTypeIds: number[],
    assetTypeIds: number[],
    config: AutoAddRFPlanConfig,
    ipDeviceStatCollection: IpDeviceStatCollection,
    time: Cesium.JulianDate,
): Radio[] {
    return radioCollection.getAllAsArray().filter((radio) => {
        const ipDevice = handleCollectionGetException(
            (id) => ipDeviceCollection.get(id),
            radio.ipDeviceId,
        ) as IpDevice | null;

        // data sanity- shouldn't happen but hey, this is real life
        if (ipDevice === null) {
            console.warn(`radio ${radio} has no parent IpDevice!`);
            return false;
        }

        const asset = handleCollectionGetException(
            (id) => assetCollection.get(id),
            ipDevice.assetId,
        ) as Asset | null;

        // this is possible and permitted data (orphaned IpDevice)- can't use it here though
        if (asset === null) {
            return false;
        }

        // filter by ipDeviceType if required
        if (ipDeviceTypeIds.length && !ipDeviceTypeIds.includes(ipDevice.typeId)) {
            return false;
        }

        // filter by assetType if required
        if (assetTypeIds.length && !assetTypeIds.includes(asset.typeId)) {
            return false;
        }

        // filter by radioStat.channel if required
        if (config.radioChannel !== RADIO_CHANNEL_OPTION.ALL_RADIOS) {
            const radioStat = getRadioStatAtTime(
                radio.id,
                radioCollection,
                ipDeviceStatCollection,
                time,
            );

            const freq = radioStat ? getRoundedFrequencyForChannel(radioStat.channel) : undefined;

            if (
                freq === Frequency._2_4_GHZ &&
                config.radioChannel === RADIO_CHANNEL_OPTION.ONLY_2_4_GHZ
            ) {
                return true;
            } else if (
                freq === Frequency._5_GHZ &&
                config.radioChannel === RADIO_CHANNEL_OPTION.ONLY_5_GHZ
            ) {
                return true;
            }

            // wrong frequency / channel
            return false;
        }

        // no further filtering required
        return true;
    });
}

export function createAutoTransmittersForRFPlanConfig(
    config: AutoAddRFPlanConfig,
    assetCollection: AssetCollection,
    ipDeviceCollection: IpDeviceCollection,
    radioCollection: RadioCollection,
    ipDeviceStatCollection: IpDeviceStatCollection,
    time: Cesium.JulianDate | null,
): Array<BoundRFTransmitterDefinition> {
    const assetTypeIds = config.assetTypes || [];
    const createForAssetsOnly = config.createPer === CREATE_PER.ASSET;
    const assets = createForAssetsOnly
        ? createAutoTransmittersForAssetsOnly(assetTypeIds, assetCollection)
        : [];

    const ipDeviceTypeIds = config.ipDeviceTypes || [];
    const createdForAssetsAndRadios = config.createPer === CREATE_PER.RADIO;
    const radios = createdForAssetsAndRadios
        ? createAutoTransmitterseForAssetsAndRadios(
              radioCollection,
              ipDeviceCollection,
              assetCollection,
              ipDeviceTypeIds,
              assetTypeIds,
              config,
              ipDeviceStatCollection,
              time,
          )
        : [];

    // build up our radio-based transmitters- this will override any asset-based transmitters
    const radioTransmitters = radios.map((radio) => {
        const ipDevice = ipDeviceCollection.get(radio.ipDeviceId);
        const assetID = ipDevice ? ipDevice.assetId : undefined;
        return {
            assetId: assetID,
            radioId: radio.id,
            name: radio.name,
        };
    });

    // build up our asset-based transmitters- NOTE: we don't add an asset-based transmitter if
    // one or more radio-based transmitters already on there
    const radioIds = radios.map((r) => r.id);
    const assetTransmitters = assets
        .filter((asset) => intersection(radioIdsForAsset(asset), radioIds).length === 0)
        .map((asset) => ({ assetId: asset.id, radioId: undefined, name: asset.name }));

    return radioTransmitters.concat(assetTransmitters);
}

function calculatedBmFrommW(mW: number): number {
    return Math.round(10 * Math.log10(mW));
}

export function getRoundedFrequencyForChannel(channel: number): Frequency | undefined {
    // nothing in channel field
    if (!channel) {
        return undefined;
    }

    // handle case wherein channel field holds a frequency (in MHz)
    if (isRadioStatChannelAFrequency(channel)) {
        if (channel > FREQUENCY_MIN_2_4_GHZ && channel < FREQUENCY_MAX_2_4_GHZ) {
            return Frequency._2_4_GHZ;
        } else if (channel > FREQUENCY_MIN_5_GHZ && channel < FREQUENCY_MAX_5_GHZ) {
            return Frequency._5_GHZ;
        }
    }

    // handle case wherein channel field holds an 802.11 / ISM band channel (e.g. channel 1 = 2412 MHz)
    if (channel < CHANNEL_CUT_OFF_2_4_GHZ) {
        return Frequency._2_4_GHZ;
    } else if (channel < CHANNEL_CUT_OFF_5_GHZ) {
        return Frequency._5_GHZ;
    }

    // unrecognised value range
    return undefined;
}

function getAbsoluteFrequencyForChannel(channel: number): number | undefined {
    if (Number.isFinite(channel) && isRadioStatChannelAFrequency(channel)) {
        // this probably isn't a channel, but the actual frequency itself
        return channel;
    }
    return CHAN_TO_FREQ[channel] || undefined;
}

export function getChannelForAbsoluteFreq(freq: number): number | null {
    if (!freq) {
        return null;
    }
    //return the value if its already a channel
    if (!isRadioStatChannelAFrequency(freq)) {
        return freq;
    }
    const result = (
        sortBy(
            Object.entries(CHAN_TO_FREQ),
            (entry: [channel: string, freq: number]) => entry[1],
        ) as Array<[string, number]>
    )
        .reverse()
        .find((v) => v[1] <= freq);

    if (result) {
        return Number(result[0]);
    }
    return null;
}

export function antFileStringToArray(antFile: string, requiredLength: number = 720): Array<string> {
    const antFileArray = antFile.trim().split('\n');
    if (antFileArray && antFileArray.length !== requiredLength) {
        throw new Error(
            `Invalid ANT File provided to RF Plan. Length must be 720. Provided length: ${antFileArray.length}`,
        );
    }
    return antFileArray;
}

export function transmitterProcessorListToTaskProcessorTask(
    rfTransProcessors: Array<RFTransmitterProcessor>,
): Task {
    return {
        type: 'rf', // matched in the web worker file
        parameters: rfTransProcessors.map((p) => ({
            heightArray: p.heightArray,
            outputArray: p.outputArray,
            gridWidth: p.gridWidth,
            gridHeight: p.gridHeight,
            gridCellWidthMeters: p.gridCellWidthMeters,
            gridCellHeightMeters: p.gridCellHeightMeters,
            rxHeightAboveGround: p.rxHeightAboveGround,
            txHeightAboveGround: p.txHeightAboveGround,
            frequencyMhz: p.frequencyMhz,
            txPowerdBm: p.txPowerdBm,
            txAntennaGain: p.txAntennaGain,
            rxAntennaGain: p.rxAntennaGain,
            coveragePlanType: p.coveragePlanType,
            minRssi: p.minRssiCutoff,
            groundHeight: p.groundHeight,
            transmitterType: p.type,
            antFileArray: p.antFile,
            horizontalAngleDeg: p.horizontalAngleDeg,
            verticalAngleDeg: p.verticalAngleDeg,
            beamWidthDeg: p.beamWidthDeg,
            beamHeightDeg: p.beamHeightDeg,
        })),
        transferableObjects: flatten(
            rfTransProcessors.map((p) => [p.heightArray.buffer, p.outputArray.buffer]),
        ),
    };
}
